import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useChipiContext } from "../context";
import type {
  CreateWalletParams,
  CreateWalletResponse,
} from "@chipi-stack/types";

type CreateWalletInput = {
  params: CreateWalletParams;
  bearerToken: string;
};

/**
 * Hook for creating a new wallet
 */
export function useCreateWallet(): {
  createWallet: (input: CreateWalletInput) => void;
  createWalletAsync: (
    input: CreateWalletInput
  ) => Promise<CreateWalletResponse>;
  data: CreateWalletResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
} {
  const { chipiSDK } = useChipiContext();

  const mutation: UseMutationResult<
    CreateWalletResponse,
    Error,
    CreateWalletInput
  > = useMutation({
    mutationFn: (input: CreateWalletInput) =>
      chipiSDK.createWallet({
        params: input.params,
        bearerToken: input.bearerToken,
      }),
  });

  return {
    createWallet: mutation.mutate,
    createWalletAsync: mutation.mutateAsync,
    data: mutation.data,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
